import { type ReactNode } from 'react'
import { RewardCard, RewardChip, CoinIcon, Tooltip } from '@repo/ui'
import { useClientSideTranslation } from 'src/services/i18n'
import { useGetCurrentLanguage } from 'src/store/game'
import { ItemCloseButton } from '../ItemCloseButton/ItemCloseButton'
import { useMobileCloseButton } from '../../hooks/useMobileCloseButton'
import { overlayVariants, closeButtonVariants } from './BonusItem.variants'

type BonusItemProps = Readonly<{
  reward: string
  isMobile?: boolean
  tooltip?: ReactNode
  onClick?: () => void
  onClose?: () => void
}>

export function BonusItem({ reward, isMobile = false, tooltip, onClick, onClose }: BonusItemProps) {
  const { t } = useClientSideTranslation(useGetCurrentLanguage())
  const { showCloseOnMobile, containerRef, handleMobileClick, handleCloseClick } = useMobileCloseButton({
    onClose,
    onClick
  })

  const sharedProps = {
    accentColor: 'green' as const,
    icon: <CoinIcon className="w-[14px] h-[14px] drop-shadow-[0_0_2px_rgba(0,0,0,0.25)]" />,
    reward
  }

  const wrapWithTooltip = (content: ReactNode) => {
    return tooltip ? (
      <Tooltip position="top" trigger={content}>{tooltip}</Tooltip>
    ) : content
  }

  // Mobile with close button
  if (isMobile && onClose) {
    return wrapWithTooltip(
      <div className="relative" ref={containerRef}>
        <RewardChip {...sharedProps} onClick={handleMobileClick} />
        <div className={overlayVariants({ visible: showCloseOnMobile })} />
        <ItemCloseButton
          className={closeButtonVariants({ visible: showCloseOnMobile, placement: 'mobile', clickable: showCloseOnMobile })}
          onClick={handleCloseClick}
        />
      </div>
    )
  }

  // Mobile without close button
  if (isMobile) {
    return wrapWithTooltip(<RewardChip {...sharedProps} onClick={onClick} />)
  }

  // Desktop with close button
  if (onClose) {
    return wrapWithTooltip(
      <div className="relative group">
        <RewardCard {...sharedProps} label={t('Free bet')} onClick={onClick} />
        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity z-[20] bg-black/60 backdrop-blur-sm rounded-[10px]" />
        <ItemCloseButton
          className={closeButtonVariants({ visible: false, placement: 'desktop', clickable: true })}
          onClick={onClose}
        />
      </div>
    )
  }

  // Desktop without close button
  return wrapWithTooltip(<RewardCard {...sharedProps} label={t('Free bet')} onClick={onClick} />)
}